import useMediaQuery from '@mui/material/useMediaQuery'
import Box from '@mui/material/Box'

// ** Layout Imports
// !Do not remove this Layout import
import Layout from 'src/@core/layouts/Layout'

// ** Navigation Imports
import VerticalNavItems from 'src/navigation/vertical'
import HorizontalNavItems from 'src/navigation/horizontal'

// ** Component Import
// Uncomment the below line (according to the layout type) when using server-side menu
// import ServerSideVerticalNavItems from './components/vertical/ServerSideNavItems'
// import ServerSideHorizontalNavItems from './components/horizontal/ServerSideNavItems'

import VerticalAppBarContent from './components/vertical/AppBarContent'
import HorizontalAppBarContent from './components/horizontal/AppBarContent'

// ** Hook Import
import { useSettings } from 'src/@core/hooks/useSettings'
import AfterVerticalNavMenuContent from './components/vertical/AfterVerticalNavMenuContent'
import { ApiEndPoints } from 'src/network/endpoints'
import { axiosInstance } from 'src/network/adapter'
import { useAuth } from 'src/hooks/useAuth'
import { toastError } from 'src/utils/utils'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

const UserLayout = ({ children }) => {
  // ** Hooks
  const { settings, saveSettings } = useSettings()
  const { t } = useTranslation()
  const { user, rolePermissions, setRolePermission } = useAuth()
  /**
   *  The below variable will hide the current layout menu at given screen size.
   *  The menu will be accessible from the Hamburger icon only (Vertical Overlay Menu).
   *  You can change the screen size from which you want to hide the current layout menu.
   *  Please refer useMediaQuery() hook: https://mui.com/material-ui/react-use-media-query/,
   *  to know more about what values can be passed to this hook.
   *  ! Do not change this value unless you know what you are doing. It can break the template.
   */
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))

  const fetchRolesAndPermissions = () => {
    axiosInstance
      .get(ApiEndPoints.ROLE.listById(user?.roleId?._id))
      .then(response => {
        const responseData = response.data.data.role.permissions
        console.log('responseData: ', responseData)
        setRolePermission(responseData)
      })
      .catch(error => {
        toastError(error)
      })
  }
  useEffect(() => {
    if (user.role === 'subAgent') {
      fetchRolesAndPermissions()
    }
  }, [])
  const getAccessibleNavItems = navItems => {
    if (user.role !== 'subAgent') {
      return navItems
    }

    // Normalize role permissions for comparison
    const normalizedPermissions = rolePermissions.map(perm => perm.toLowerCase())

    // Check if user has "Payments & Subscriptions" permission
    const hasPaymentsSubscriptions = normalizedPermissions.includes('payments & subscriptions')

    return navItems
      .map(item => {
        // Always allow Dashboard
        if (item.title === 'Dashboard') {
          return item
        }

        // Special handling for Payments & Subscriptions permission
        // If user has "Payments & Subscriptions" permission, show both Payments and Oujat Protection
        if (hasPaymentsSubscriptions) {
          if (item.id === 'payments' || item.id === 'subscriptions' || item.id === 'oujat_protection') {
            return item
          }
        }

        // Normalize the item title (remove prefixes like "My", "Open")
        const normalizedTitle = item.title
          .replace(/^(My|Open)\s+/i, '') // Remove leading "My " or "Open "
          .trim()
          .toLowerCase()

        // Check if normalized title is in the role permissions
        const hasPermission = normalizedPermissions.includes(normalizedTitle)

        return hasPermission ? item : null
      })
      .filter(Boolean)
  }

  return (
    <Layout
      hidden={hidden}
      settings={settings}
      saveSettings={saveSettings}
      {...(settings.layout === 'horizontal'
        ? {
            // ** Navigation Items
            horizontalNavItems: HorizontalNavItems(),

            // Uncomment the below line when using server-side menu in horizontal layout and comment the above line
            // horizontalNavItems: ServerSideHorizontalNavItems(),
            // ** AppBar Content
            horizontalAppBarContent: () => (
              <HorizontalAppBarContent hidden={hidden} settings={settings} saveSettings={saveSettings} />
            )
          }
        : {
            // ** Navigation Items
            // verticalNavItems: VerticalNavItems(),
            verticalNavItems: getAccessibleNavItems(VerticalNavItems(t)),
            afterVerticalNavMenuContent: AfterVerticalNavMenuContent,

            // Uncomment the below line when using server-side menu in vertical layout and comment the above line
            // verticalNavItems: ServerSideVerticalNavItems(),
            // ** AppBar Content
            verticalAppBarContent: props => (
              <VerticalAppBarContent
                hidden={hidden}
                settings={settings}
                saveSettings={saveSettings}
                toggleNavVisibility={props.toggleNavVisibility}
              />
            )
          })}
    >
      {user?.isReadOnly ? (
        <Box sx={{ px: 2, pt: 2 }}>
          <Box
            sx={{
              border: '1px solid #B7D7F8',
              backgroundColor: '#EAF4FF',
              color: '#0B3D6E',
              borderRadius: '8px',
              px: 2,
              py: 1.5,
              fontSize: '0.95rem',
              fontWeight: 500,
              mb: 3
            }}
          >
            Your account is pending approval. You can browse global requests, but responding to requests, customer chat
            and storefront setup are disabled until approval.
          </Box>
        </Box>
      ) : null}
      {children}
    </Layout>
  )
}

export default UserLayout
